import { useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, ArrowLeft, ArrowRight, Languages, ChevronLeft, ChevronRight } from "lucide-react";
import { useLang } from "@/i18n";
import { serviceBySlug, SERVICES } from "@/data/servicesData";
import WhatsAppFloat from "@/components/site/WhatsAppFloat";

export default function ServicePage() {
  const { slug } = useParams();
  const { t, lang, setLang, dir } = useLang();
  const service = serviceBySlug(slug);

  useEffect(() => {
    if (service) {
      document.title = `${service.name[lang]} | ${t("brand")} ${t("tagline")}`;
      window.scrollTo(0, 0);
    }
  }, [service, lang, t]);

  if (!service) return <Navigate to="/" replace />;

  const Icon = service.icon;
  const Arrow = dir === "rtl" ? ArrowRight : ArrowLeft;
  const Chevron = dir === "rtl" ? ChevronLeft : ChevronRight;
  const others = SERVICES.filter((s) => s.slug !== slug).slice(0, 5);

  return (
    <div data-testid="service-page" className="min-h-screen bg-navy-900 relative">
      <div className="noise-overlay" />
      <header className="fixed top-0 inset-x-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
          <Link to="/" data-testid="service-logo" className="flex items-center gap-3">
            <img src="/logo.png" alt="Barq Safe" className="h-11 w-11 object-contain rounded-xl bg-white/95 p-1" />
            <div className="leading-tight">
              <span className="block font-display font-extrabold">{t("brand")}</span>
              <span className="block text-[10px] tracking-widest text-gold">{t("tagline")}</span>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <button data-testid="service-lang-toggle" onClick={() => setLang(lang === "ar" ? "en" : "ar")}
              className="flex items-center gap-2 px-3 py-2 rounded-full border border-gold/30 text-gold text-xs font-semibold hover:bg-gold/10 transition-colors">
              <Languages size={14} />
              {lang === "ar" ? "EN" : "عربي"}
            </button>
            <Link to="/" data-testid="service-back-link"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/15 text-white/70 text-sm hover:border-gold/50 hover:text-gold transition-colors">
              <Arrow size={15} />
              {t("sp_back")}
            </Link>
          </div>
        </div>
      </header>

      <section className="relative pt-40 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-navy-800/60 to-navy-900" />
        <div className="absolute -top-20 -end-20 w-96 h-96 rounded-full bg-gold/10 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <div className="w-16 h-16 rounded-2xl bg-gold/10 border border-gold/30 flex items-center justify-center text-gold mb-8">
              <Icon size={30} />
            </div>
            <span className="block text-xs md:text-sm tracking-[0.3em] uppercase text-gold font-semibold mb-4">{t("nav_services")}</span>
            <h1 className="font-display font-black text-4xl md:text-5xl lg:text-6xl tracking-tight max-w-3xl" data-testid="service-title">{service.name[lang]}</h1>
            <p className="mt-4 font-display text-xl md:text-2xl gold-text font-bold" data-testid="service-tagline">{service.tagline[lang]}</p>
            <p className="mt-6 text-white/65 text-base md:text-lg leading-relaxed max-w-3xl" data-testid="service-intro">{service.intro[lang]}</p>
            <div className="mt-10 flex flex-wrap gap-4">
              <a href="/#contact" data-testid="service-cta-primary"
                className="inline-flex items-center gap-3 px-8 py-4 rounded-full bg-gold text-navy-900 font-bold hover:bg-gold-light transition-colors shadow-xl shadow-gold/20">
                {t("sp_cta_btn")}
              </a>
              <a href="https://wa.me/966579462600" target="_blank" rel="noreferrer" data-testid="service-cta-whatsapp"
                className="inline-flex items-center gap-3 px-8 py-4 rounded-full border border-white/20 text-white font-semibold hover:border-gold/60 hover:text-gold transition-colors">
                {t("contact_whatsapp")}: <span dir="ltr">+966 57 946 2600</span>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-20 grid lg:grid-cols-2 gap-10">
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }}
          className="glass rounded-3xl p-8 md:p-10" data-testid="service-included">
          <h2 className="font-display font-extrabold text-2xl md:text-3xl mb-8">{t("sp_included")}</h2>
          <ul className="space-y-4">
            {service.included[lang].map((item, i) => (
              <li key={i} className="flex items-start gap-3 text-white/80 text-sm md:text-base" data-testid={`service-included-${i}`}>
                <CheckCircle2 size={20} className="text-gold shrink-0 mt-0.5" />
                {item}
              </li>
            ))}
          </ul>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8, delay: 0.1 }}
          className="glass rounded-3xl p-8 md:p-10" data-testid="service-steps">
          <h2 className="font-display font-extrabold text-2xl md:text-3xl mb-8">{t("sp_steps")}</h2>
          <div className="space-y-7">
            {service.steps[lang].map((step, i) => (
              <div key={i} className="flex items-start gap-5" data-testid={`service-step-${i}`}>
                <span className="font-display text-4xl font-black text-gold/25 leading-none shrink-0 w-12">{String(i + 1).padStart(2, "0")}</span>
                <p className="text-white/75 text-sm md:text-base leading-relaxed pt-1.5">{step}</p>
              </div>
            ))}
          </div>
        </motion.div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-24">
        <h2 className="font-display font-extrabold text-2xl mb-8">{t("sp_all_services")}</h2>
        <div className="flex flex-wrap gap-3" data-testid="service-related">
          {others.map((s) => (
            <Link key={s.slug} to={`/services/${s.slug}`} data-testid={`related-${s.slug}`}
              className="group inline-flex items-center gap-2 px-5 py-3 rounded-full glass text-sm text-white/70 hover:text-gold hover:border-gold/40 transition-colors">
              {s.name[lang]}
              <Chevron size={14} className="transition-transform rtl:group-hover:-translate-x-1 ltr:group-hover:translate-x-1" />
            </Link>
          ))}
        </div>
      </section>

      <div className="border-t border-gold/15 py-6 text-center text-xs text-white/40">© 2026 {t("footer_rights")}</div>
      <WhatsAppFloat />
    </div>
  );
}
